/**
 * Reporting duties after a workplace accident, per country.
 *
 * Shared source of truth for the DTO validation, the config screen's preset
 * picker and the deadline shown on an incident. Applied client-side: a preset
 * only fills the organisation's own fields, it creates nothing — so there is
 * no reason to round-trip to the server to read it.
 *
 * Figures researched from each regulator's own text (see the per-country
 * notes) and should get a final legal sanity check before an organisation
 * relies on them. They are a starting point the admin can overwrite: every
 * field stays editable after a preset is applied.
 */
export const INCIDENT_REGULATION_COUNTRIES = [
  'FR',
  'DE',
  'ES',
  'IT',
  'GB',
  'US',
  'CA',
] as const;

export type IncidentRegulationCountry =
  (typeof INCIDENT_REGULATION_COUNTRIES)[number];

/**
 * What a reporting deadline counts from. Regulators do not agree on this, and
 * the difference is a whole day or more: OSHA and the Berufsgenossenschaft
 * start the clock when the employer *learns* of the accident, RIDDOR and the
 * Spanish parte start it at the accident itself.
 *
 * Two values, because they are the two instants an incident record actually
 * holds: `occurredAt`, what the declarant says happened when, and `declaredAt`,
 * the moment the declaration reached GearTracker. The Italian trigger — the
 * medical certificate landing on the employer's desk — has no field of its
 * own; `KNOWLEDGE` is the closest honest reading and the preset says so.
 */
export const INCIDENT_DEADLINE_TRIGGER_VALUES = [
  'OCCURRENCE',
  'KNOWLEDGE',
] as const;

export type IncidentDeadlineTrigger =
  (typeof INCIDENT_DEADLINE_TRIGGER_VALUES)[number];

export type IncidentRegulationPreset = {
  /**
   * Hours to report an ordinary workplace accident, counted from
   * `deadlineTrigger`. `null` would mean no duty at all — no shipped preset
   * uses it, but an organisation can set it, which is why the field is
   * nullable.
   */
  declarationDeadlineHours: number | null;
  /**
   * Hours to report a fatal or serious accident. `0` means "at once": several
   * regulators require a phone call with no grace period at all, and rounding
   * that up to a day would be wrong in the one case where it matters.
   */
  fatalDeadlineHours: number | null;
  /** What both deadlines are counted from. */
  deadlineTrigger: IncidentDeadlineTrigger;
  /** Who the report goes to. Proper nouns — not translated. */
  authority: string;
};

/**
 * Statutory deadlines are often written in working days; we store plain hours
 * (the config screen has no working-day calendar), so those are converted at
 * 24 h per day, which is always the stricter reading and never promises more
 * time than the law gives.
 */
export const INCIDENT_REGULATION_PRESETS: Record<
  IncidentRegulationCountry,
  IncidentRegulationPreset
> = {
  // Art. R. 441-3 CSS: 48 h (excluding Sundays and public holidays) to the
  // CPAM. Art. R. 4121-5 C. trav. (décret 2023-452): 12 h to the labour
  // inspectorate when the worker dies.
  // The 48 h run from the moment the employer is told, not from the accident.
  FR: {
    declarationDeadlineHours: 48,
    fatalDeadlineHours: 12,
    deadlineTrigger: 'KNOWLEDGE',
    authority: 'CPAM / MSA — inspection du travail',
  },
  // § 193 SGB VII: Unfallanzeige within 3 days once the accident costs more
  // than 3 days of work. Fatal, severe or mass accidents: unverzüglich.
  // Counted from the employer's knowledge of the accident (Kenntnis).
  DE: {
    declarationDeadlineHours: 72,
    fatalDeadlineHours: 0,
    deadlineTrigger: 'KNOWLEDGE',
    authority: 'Berufsgenossenschaft / Unfallkasse',
  },
  // Parte de accidente via Delt@ within 5 working days. Serious, very serious,
  // fatal, or affecting more than four workers: 24 h to the autoridad laboral.
  // Counted from the accident itself (the baja médica dates from it).
  ES: {
    declarationDeadlineHours: 120,
    fatalDeadlineHours: 24,
    deadlineTrigger: 'OCCURRENCE',
    authority: 'Mutua / Delt@ — autoridad laboral',
  },
  // Denuncia di infortunio within 2 days of the medical certificate, for a
  // prognosis over 3 days. Fatal or life-threatening: 24 h.
  // Counted from the medical certificate reaching the employer, which we can
  // only approximate by the moment the declaration reaches us.
  IT: {
    declarationDeadlineHours: 48,
    fatalDeadlineHours: 24,
    deadlineTrigger: 'KNOWLEDGE',
    authority: 'INAIL',
  },
  // RIDDOR: specified injuries reported within 10 days (over-seven-day
  // injuries get 15, so 10 is the stricter figure to hold). Deaths and
  // specified injuries must also be phoned through without delay.
  // RIDDOR counts its days from the incident, not from the report reaching
  // the employer.
  GB: {
    declarationDeadlineHours: 240,
    fatalDeadlineHours: 0,
    deadlineTrigger: 'OCCURRENCE',
    authority: 'HSE (RIDDOR)',
  },
  // 29 CFR 1904.39: 24 h for an in-patient hospitalisation, amputation or
  // loss of an eye; 8 h for a fatality.
  // 1904.39(b)(6): the clock starts when the employer learns of the event.
  US: {
    declarationDeadlineHours: 24,
    fatalDeadlineHours: 8,
    deadlineTrigger: 'KNOWLEDGE',
    authority: 'OSHA',
  },
  // Provincial, with Ontario as the reference: Form 7 to the WSIB within 3
  // working days, and a critical injury or death phoned to the ministry
  // immediately. An org in another province should adjust both figures.
  // Ontario's three days run from learning of the accident.
  CA: {
    declarationDeadlineHours: 72,
    fatalDeadlineHours: 0,
    deadlineTrigger: 'KNOWLEDGE',
    authority: 'Commission des accidents du travail (ex. WSIB Ontario)',
  },
};
